// THE event icon outside the map canvas (docs/SHARED.md "One component per look"; web/pins.js emojiBadge, ios
// EmojiBadge): the event's emoji in its pin's look (RULES.pinStyle: green now / blue soon, squircle or ticket notch)
// with its price badge (PriceTag) when paid. Every list row, card, search result, My events row and the card's header
// uses this; sizes are its named variants. Don't build one by hand.
import type { CSSProperties } from "react";
import type { FeedEvent } from "../data/event";
import { shapeSVG } from "../map/draw";
import { SHARED } from "../shared/config";
import { RULES } from "../shared/rules";
import { useNow } from "../state/clock";
import { emojiOf, useFeed } from "../state/feed";
import { EmojiGlyph } from "./EmojiGlyph";
import styles from "./EmojiBadge.module.css";

const SIZES = { small: { box: 40, emoji: 21 }, normal: { box: 46, emoji: 25 } } as const;
export type BadgeSize = keyof typeof SIZES;

export function EmojiBadge({ event, size = "small" }: { event: FeedEvent; size?: BadgeSize }) {
  const now = useNow();
  useFeed((s) => s.tagEmoji); // the emoji can change when the types arrive
  const style = RULES.pinStyle(event, now, { withPrice: true }), b = SIZES[size], r = b.box / 2;
  return (
    <span className={styles.badge} style={{ width: b.box, height: b.box }} aria-hidden="true">
      <svg className={styles.shape} viewBox={`${-r} ${-r} ${2 * r} ${2 * r}`} dangerouslySetInnerHTML={{ __html: shapeSVG(style, r) }} />
      <EmojiGlyph emoji={emojiOf(event)} px={b.emoji} />
      {style.price && <PriceTag price={style.price} />}
    </span>
  );
}

// THE price badge (RULES.pinPrice; nowish.json pins.price; ios PinPriceLabel): a paid event's lowest price on a
// little label overlapping its icon's bottom-right corner. Here and on the selected pin; never on ordinary map pins.
const P = SHARED.pins.price;
const tokenVar = (t: string) => `var(--${t.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)})`;
export const PRICE_VARS = {
  "--pin-price-font": `${P.font}px`, "--pin-price-height": `${P.height}px`, "--pin-price-pad": `${P.padX}px`,
  "--pin-price-overhang": `${-P.overhang}px`, "--pin-price-color": tokenVar(P.color), "--pin-price-bg": tokenVar(P.background),
} as CSSProperties;
export function PriceTag({ price, className }: { price: string; className?: string }) {
  return <span className={className ? `${styles.price} ${className}` : styles.price} style={PRICE_VARS}>{price}</span>;
}

/** The same label as a plain element, for the map's markers (the selected pin), which aren't React. */
export function priceTagElement(price: string) {
  const el = document.createElement("span");
  el.className = `${styles.price} pin-price`;
  for (const [k, v] of Object.entries(PRICE_VARS)) el.style.setProperty(k, String(v));
  el.textContent = price;
  return el;
}
